import { useEffect, useRef, useState } from 'react';
import { BsChat, BsPencil, BsTrash } from 'react-icons/bs';
import ImageUploading from 'react-images-uploading';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import ChatbotIcon from '@/assets/all-icons/chatbot-icon';
import DeleteIcon from '@/assets/all-icons/delete-icon';
import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import PopupUpdateArticleCover from '@/pages/artwork/artwork-header-mobile/options/download-template/components/PopupUpdateArticleCover';
import SubscriptionPreview from '@/pages/profile/channels/SubscriptionPreview';
import ChannelsService from '@/services/channels.service';
import fetchWrapper from '@/services/fetchWrapper.service';
import { auth } from '@/services/firebase.service';
import {
  getPendingGalleryQrVisit,
  redeemGalleryQrVisit,
  setPendingGalleryQrVisit,
} from '@/services/galleryQrVisits.service';
import SupportService from '@/services/support.service';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import PullToRefresh from '@/shared-components/common/components/PullToRefresh';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Loader from '@/shared-components/loader/Loader';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { normalizeProfileRouteOverrideMap, normalizeProfileRouteTitleOverrideMap } from '@/utils/seoRouteOverrides';
import { getSsrProfileBootstrap } from '@/utils/ssrProfileBootstrap';

import DisplayDescription from './components/DisplayDescription';
import DisplayLocation from './components/DisplayLocation';
import DisplayNameUsername from './components/DisplayNameUsername';
import MetaDescriptionUsers from './components/MetaDescriptionUsers';
import ProfileAskAi from './components/ProfileAskAi';
import ProfileButton from './components/ProfileButton';
import ProfileNavigation from './components/ProfileNavigation';
import UserNotFound from './components/UserNotFound';
import { normaliseSupportConfig } from './support/constants';
import ProfileStats from './profile-stats';
import ProfileTopButtons from './profile-top-buttons';

import './index.css';

const PROFILE_CHANNEL_LINK_ENABLED = true; // Re-enable gallery channel entry point on profiles
const ALLOW_MANUAL_CHANNEL_MANAGEMENT = false;
const CHANNEL_CURRENCY_OPTIONS = Array.isArray(config?.forms?.currency_options)
  ? config.forms.currency_options
  : ['USD', 'EUR', 'GBP'];
const DEFAULT_CHANNEL_CURRENCY = CHANNEL_CURRENCY_OPTIONS[0] || 'USD';
const isPublishedChannel = (channel) => (
  typeof channel?.is_published === 'boolean' ? channel.is_published : true
);

function resolvePublicProfileSubpageSeoRequest(pathname, search, username) {
  const normalizedUsername = typeof username === 'string' ? username.trim() : '';
  if (!normalizedUsername) {
    return null;
  }

  const basePath = `/user/${normalizedUsername}`;
  if (!pathname.startsWith(`${basePath}/`)) {
    return null;
  }

  const subPage = pathname.slice(basePath.length + 1).split('/')[0]?.trim().toLowerCase();
  if (!['artworks', 'artists', 'collection', 'artlists', 'community', 'board'].includes(subPage)) {
    return null;
  }

  const params = new URLSearchParams(search || '');
  const boardView = params.get('view')?.trim().toLowerCase() === 'articles' ? 'articles' : 'posts';
  const communityView = params.get('v')?.trim().toLowerCase() === 'ratings' ? 'ratings' : 'thoughts';
  const routeKey = `${pathname}${search || ''}`;
  const query = subPage === 'board'
    ? `?view=${boardView}`
    : subPage === 'community'
      ? `?v=${communityView}`
      : '';

  return {
    routeKey,
    url: `${config.apis.api.url}/seo/profile/${encodeURIComponent(normalizedUsername)}/subpage/${subPage}/robots${query}`,
  };
}

function resolveChannelCurrency(rawCurrency) {
  if (typeof rawCurrency !== 'string') return DEFAULT_CHANNEL_CURRENCY;
  const normalized = rawCurrency.trim().toUpperCase();
  return CHANNEL_CURRENCY_OPTIONS.includes(normalized)
    ? normalized
    : DEFAULT_CHANNEL_CURRENCY;
}

function UserProfileRenderView({ profileUsername, internal }) {
  const t = useTranslations('common.profile.layout');
  const { cacheHandler, stateHandler, state } = useStateHandler();
  const navigate = useNavigate();
  const location = useLocation();
  const ssrBootstrap = getSsrProfileBootstrap(profileUsername);
  const forceNotFound = !internal && ssrBootstrap?.forceNotFound === true;
  const [channelsPopupOpen, setChannelsPopupOpen] = useState(false);
  const [previewChannel, setPreviewChannel] = useState(null);
  const [channelsList, setChannelsList] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [hasCreatedChannels, setHasCreatedChannels] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    title: '',
    type: 'public',
    description: '',
    image: '',
    price: '',             // NEW
    currency: DEFAULT_CHANNEL_CURRENCY,
    write_perms: 'everyone'// NEW
  });
  const [creatingNew, setCreatingNew] = useState(false);
  const [newDraft, setNewDraft] = useState({
    title: '',
    type: 'public',
    description: '',
    image: '',
    price: '',             // NEW
    currency: DEFAULT_CHANNEL_CURRENCY,
    write_perms: 'everyone'// NEW
  });
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [openCoverCrop, setOpenCoverCrop] = useState(false);
  const [selectedCoverFile, setSelectedCoverFile] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null); // 'new' | 'edit'
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [channelPendingDelete, setChannelPendingDelete] = useState(null);
  const [deletingChannel, setDeletingChannel] = useState(false);
  const [profileAiMessages, setProfileAiMessages] = useState([]);
  const [isProfileAiOpen, setIsProfileAiOpen] = useState(false);
  const [aiCapability, setAiCapability] = useState({
    enabled: false,
    assistant_name: 'SEA',
  });
  const imageUploadRef = useRef(null);
  const { fetchData, setFetchData, refetch } = useCache(
    `${profileUsername}_userProfileInfo`,
    `${config.apis.api.url}/user/username/${profileUsername}`,
    {
      type: "@cache/dynamic",
      injectToken: true,
      initialData: !internal ? ssrBootstrap?.data ?? null : null,
      disabled: forceNotFound,
    }
  );
  const setFetchDataRef = useRef(setFetchData);
  const normalizedProfileType = String(fetchData?.user_type || fetchData?.__t || '').toLowerCase();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('gallery_qr')?.trim() || '';
    if (tokenFromUrl) {
      setPendingGalleryQrVisit(tokenFromUrl);
    }

    let active = true;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      const pendingToken = tokenFromUrl || getPendingGalleryQrVisit();
      if (!user || !pendingToken) return;

      try {
        await redeemGalleryQrVisit(pendingToken);
        if (!active) return;
        setPendingGalleryQrVisit('');

        if (tokenFromUrl) {
          const nextParams = new URLSearchParams(location.search);
          nextParams.delete('gallery_qr');
          const nextSearch = nextParams.toString();
          navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
        }
      } catch (error) {
        // Keep the pending token for a later authenticated retry. A QR scan must never block profile access.
        console.warn('Unable to record gallery QR visit', error);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    setFetchDataRef.current = setFetchData;
  }, [setFetchData]);

  useEffect(() => {
    if (internal || !fetchData || fetchData.__seo) {
      return;
    }

    const isPublicBaseRoute =
      location.pathname === `/user/${profileUsername}`
      || location.pathname === `/user/${profileUsername}/`;

    if (!isPublicBaseRoute) {
      return;
    }

    let isSubscribed = true;

    (async () => {
      try {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/seo/profile/${profileUsername}`,
          { injectToken: true },
        );

        if (!isSubscribed || !response?.ok || !data) {
          return;
        }

        setFetchDataRef.current(prev => (prev ? { ...prev, __seo: data } : prev));
      } catch (error) {
        if (isSubscribed) {
          console.error('Failed to load profile seo metadata', error);
        }
      }
    })();

    return () => {
      isSubscribed = false;
    };
  }, [fetchData, internal, location.pathname, profileUsername]);

  useEffect(() => {
    if (internal || !fetchData || fetchData.__seoProfileRouteOverrides) {
      return;
    }

    let isSubscribed = true;

    (async () => {
      try {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/seo/profile-route-overrides`,
        );

        const normalizedOverrides = normalizeProfileRouteOverrideMap(data);
        const normalizedTitleOverrides = normalizeProfileRouteTitleOverrideMap(data);

        if (!isSubscribed || !response?.ok || (Object.keys(normalizedOverrides).length === 0 && Object.keys(normalizedTitleOverrides).length === 0)) {
          return;
        }

        setFetchDataRef.current(prev => (prev ? {
          ...prev,
          __seoProfileRouteOverrides: normalizedOverrides,
          __seoProfileRouteTitleOverrides: normalizedTitleOverrides,
        } : prev));
      } catch (error) {
        if (isSubscribed) {
          console.error('Failed to load profile route seo overrides', error);
        }
      }
    })();

    return () => {
      isSubscribed = false;
    };
  }, [fetchData, internal]);

  useEffect(() => {
    if (internal || !fetchData) {
      return;
    }

    const seoRequest = resolvePublicProfileSubpageSeoRequest(
      location.pathname,
      location.search,
      profileUsername,
    );

    if (!seoRequest) {
      return;
    }

    if (fetchData.__seoProfileSubpageRobots?.routeKey === seoRequest.routeKey) {
      return;
    }

    let isSubscribed = true;

    (async () => {
      try {
        const { response, data } = await fetchWrapper.get(seoRequest.url, {
          injectToken: true,
        });

        if (!isSubscribed || !response?.ok || !data?.robots) {
          return;
        }

        setFetchDataRef.current(prev => (prev ? {
          ...prev,
          __seoProfileSubpageRobots: {
            routeKey: seoRequest.routeKey,
            robots: data.robots,
          },
        } : prev));
      } catch (error) {
        if (isSubscribed) {
          console.error('Failed to load profile subpage robots metadata', error);
        }
      }
    })();

    return () => {
      isSubscribed = false;
    };
  }, [fetchData, internal, location.pathname, location.search, profileUsername]);

  useEffect(() => {
    const userId = fetchData?._id || fetchData?.user_id;
    if (!userId) {
      return;
    }

    const hasResolvedProfileType = Boolean(normalizedProfileType);
    if (!hasResolvedProfileType) {
      return;
    }

    const isSupportEligible = normalizedProfileType === 'artist' || normalizedProfileType === 'gallery';
    const hasExistingSupportSettings = Boolean(fetchData?.support_settings);

    if (!isSupportEligible) {
      if (fetchData?.support_settings) {
        setFetchDataRef.current(prev => (prev ? { ...prev, support_settings: null } : prev));
      }
      return;
    }

    if (hasExistingSupportSettings) {
      return;
    }

    let isSubscribed = true;
    let retryTimeoutId = null;

    const loadSupportConfiguration = async (attempt = 0) => {
      try {
        const { response, data } = await SupportService.getConfiguration(userId, { force: true });
        if (!isSubscribed) {
          return;
        }

        if (response?.ok && data) {
          setFetchDataRef.current(prev => (prev ? { ...prev, support_settings: normaliseSupportConfig(data) } : prev));
          return;
        }

        if (attempt < 1) {
          retryTimeoutId = setTimeout(() => {
            loadSupportConfiguration(attempt + 1);
          }, 700);
          return;
        }

        if (!hasExistingSupportSettings) {
          setFetchDataRef.current(prev => (prev ? { ...prev, support_settings: normaliseSupportConfig(null) } : prev));
        }
      } catch (error) {
        if (isSubscribed) {
          console.error('Failed to load support configuration', error);
          if (attempt < 1) {
            retryTimeoutId = setTimeout(() => {
              loadSupportConfiguration(attempt + 1);
            }, 700);
          }
        }
      }
    };

    loadSupportConfiguration();

    return () => {
      isSubscribed = false;
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [fetchData?._id, fetchData?.support_settings, normalizedProfileType]);

  useEffect(() => {
    if (!internal || !fetchData) {
      return;
    }

    const sessionId = state.user_session?._id || state.user_session?.user_id;
    const profileId = fetchData?._id || fetchData?.user_id;
    if (!sessionId || sessionId !== profileId) {
      return;
    }

    const profileSuartePlus = fetchData?.suartePlus;
    const sessionSuartePlus = state.user_session?.suartePlus;
    const nextSessionSuartePlus = profileSuartePlus?.isActive ? profileSuartePlus : null;

    if (!nextSessionSuartePlus && !sessionSuartePlus) {
      return;
    }

    if (
      nextSessionSuartePlus?.isActive &&
      sessionSuartePlus?.isActive &&
      sessionSuartePlus?.id &&
      sessionSuartePlus?.id === nextSessionSuartePlus?.id
    ) {
      return;
    }

    stateHandler.set("user_session.suartePlus", nextSessionSuartePlus);
  }, [internal, fetchData, state.user_session, stateHandler]);

  useEffect(() => {
    const userId = fetchData?._id || fetchData?.user_id;
    const isArtistProfile = normalizedProfileType === 'artist';
    const isGalleryProfile = normalizedProfileType === 'gallery';

    if (!userId || (!isArtistProfile && !isGalleryProfile)) {
      setAiCapability({ enabled: false, assistant_name: 'SEA' });
      setProfileAiMessages([]);
      setIsProfileAiOpen(false);
      return;
    }

    let isSubscribed = true;
    (async () => {
      try {
        const capabilityUrl = isArtistProfile
          ? `${config.apis.api.url}/ai/users/${userId}/capability`
          : `${config.apis.api.url}/ai/galleries/${userId}/capability`;

        const { response, data } = await fetchWrapper.get(
          capabilityUrl,
        );
        if (!isSubscribed) {
          return;
        }
        if (!response?.ok) {
          setAiCapability({ enabled: false, assistant_name: 'SEA' });
          return;
        }
        setAiCapability({
          enabled: Boolean(data?.enabled),
          assistant_name:
            typeof data?.assistant_name === 'string' && data.assistant_name.trim()
              ? data.assistant_name.trim()
              : 'SEA',
        });
      } catch (error) {
        if (isSubscribed) {
          setAiCapability({ enabled: false, assistant_name: 'SEA' });
        }
      }
    })();

    return () => {
      isSubscribed = false;
    };
  }, [fetchData?._id, fetchData?.user_id, normalizedProfileType, setProfileAiMessages]);

  async function fetchChannelsForOwner() {
    try {
      const ownerId = fetchData?._id || fetchData?.user_id;
      if (!ownerId) return { response: null, data: null };
      const { response, data } = await ChannelsService.getByOwner(ownerId);
      if (!response?.ok) return { response, data };
      const channels = Array.isArray(data?.data) ? data.data : [];
      setChannelsList(channels);
      return { response, data };
    } finally {
      // noop
    }
  }

  function resetChannelsPopupState() {
    setEditingChannelId(null);
    setCreatingNew(false);
    setCoverPreviewUrl(null);
    setSelectedCoverFile(null);
    setOpenCoverCrop(false);
    setUploadTarget(null);
  }

  function openDeleteChannelDialog(channel) {
    setChannelPendingDelete(channel);
    setDeleteDialogOpen(true);
  }

  function closeDeleteChannelDialog() {
    if (deletingChannel) return;
    setDeleteDialogOpen(false);
    setChannelPendingDelete(null);
  }

  async function confirmDeleteChannel() {
    const channelId = channelPendingDelete?._id;
    if (!channelId || deletingChannel) return;

    setDeletingChannel(true);
    try {
      const { response } = await ChannelsService.remove(channelId);
      if (response?.ok) {
        await fetchChannelsForOwner();
      }
      closeDeleteChannelDialog();
    } finally {
      setDeletingChannel(false);
    }
  }

  async function handleOpenChannels() {
    resetChannelsPopupState();
    setChannelsLoading(true);
    try {
      const { data } = await fetchChannelsForOwner();
      const channels = Array.isArray(data?.data) ? data.data : [];
      if (channels.length > 0) {
        await handleOpenChannel(channels[0]);
        return;
      }
      setChannelsPopupOpen(true);
    } finally {
      setChannelsLoading(false);
    }
  }

  async function handleOpenChannel(channel) {
    setChannelsPopupOpen(false);

    if (internal) {
      navigate(`/channel/${fetchData.user_username}/${channel._id}`);
      return;
    }

    try {
      const { response, data } = await ChannelsService.getMemberOf('me');
      const memberList = Array.isArray(data?.data) ? data.data : [];
      const isMember = memberList.some((item) => String(item?._id) === String(channel._id));

      if (response?.ok && isMember) {
        navigate(`/channel/${fetchData.user_username}/${channel._id}`);
      } else {
        setPreviewChannel(channel);
      }
    } catch (e) {
      setPreviewChannel(channel);
    }
  }

  useEffect(() => {
    const ownerId = fetchData?._id || fetchData?.user_id;
    const canHaveChannel = normalizedProfileType === 'gallery' || normalizedProfileType === 'artist';

    if (!PROFILE_CHANNEL_LINK_ENABLED || internal || !ownerId || !canHaveChannel) {
      setHasCreatedChannels(false);
      return;
    }

    let isSubscribed = true;
    (async () => {
      try {
        const { response, data } = await ChannelsService.getByOwner(ownerId);
        if (!isSubscribed) {
          return;
        }
        if (!response?.ok) {
          setHasCreatedChannels(false);
          return;
        }
        const channels = Array.isArray(data?.data) ? data.data : [];
        setHasCreatedChannels(channels.some((channel) => isPublishedChannel(channel)));
      } catch (_) {
        if (isSubscribed) {
          setHasCreatedChannels(false);
        }
      }
    })();

    return () => {
      isSubscribed = false;
    };
  }, [fetchData?._id, fetchData?.user_id, internal, normalizedProfileType]);

  async function handleCoverCropped(croppedImageFile) {
    try {
      setOpenCoverCrop(false);
      // immediate local preview
      const blobUrl = URL.createObjectURL(croppedImageFile);
      setCoverPreviewUrl(blobUrl);
      setCoverUploading(true);

      // upload to image service
      const { response, data } = await UserService.uploadImage(croppedImageFile);
      if (response?.ok && data?.image_id) {
        if (uploadTarget === 'edit') {
          setEditDraft(d => ({ ...d, image: data.image_id }));
        } else {
          setNewDraft(d => ({ ...d, image: data.image_id }));
        }
      } else {
        alert('Error uploading cover image. Please try again.');
      }
    } finally {
      setCoverUploading(false);
    }
  }

  if (forceNotFound) {
    return (
      <UserNotFound />
    );
  } else if (!fetchData) {
    return (
      <Loader className="user-profile__profile-loading-indicator" thin/>
    );
  } else if (fetchData.error_type === "NOT_FOUND") {
    return (
      <UserNotFound />
    );
  } else {
    const isClaimedArtistProfile = normalizedProfileType === 'artist';
    const isGalleryProfile = normalizedProfileType === 'gallery';
    const shouldShowAskAiButton =
      (isClaimedArtistProfile && aiCapability.enabled === true)
      || (isGalleryProfile && !internal && aiCapability.enabled === true);

    return (
      <>
        <MetaDescriptionUsers fetchData={fetchData} internal={internal} />
        <ProfileDataContext.Provider
          value={{
            profileData: fetchData,
            setProfileData: setFetchData,
            internal,
            profileAiMessages,
            setProfileAiMessages,
          }}
        >
          <AppNavigationPage>
            <PullToRefresh
              className="user-profile-pull-to-refresh-background"
              screenThreshold={160}
              type="DRAGGABLE"
              onRefresh={() => {
                cacheHandler.triggerAction(`${fetchData._id}_REFRESH`);
                return refetch();
              }}
            >
              <div className="user-profile__wrap-outter">
                <div className="user-profile__wrap">
                  <div className="user-profile__main-content">
                    <UserProfileImage
                      image={fetchData.user_image?.image_id}
                      typeOfProfile={fetchData.user_type}
                      className="user-profile__user-image"
                      style={{ viewTransitionName: `user_${fetchData._id}_animation` }}
                      userFlags={fetchData.user_flags}
                      collectorFrameThemeId={
                        fetchData.user_flags?.collector_frame_theme ||
                        fetchData.user_flags?.collector_frame_theme_id
                      }
                      showCollectorBadge
                    />

                    <DisplayNameUsername fetchData={fetchData} />
                    <DisplayDescription fetchData={fetchData}/>

                    {PROFILE_CHANNEL_LINK_ENABLED
                      && (normalizedProfileType === 'gallery' || normalizedProfileType === 'artist')
                      && (internal || hasCreatedChannels) && (
                      <div className="user-profile__channel-link-container">
                        <button
                          type="button"
                          onClick={handleOpenChannels}
                          className="user-profile__channel-link"
                          disabled={channelsLoading}
                        >
                          <BsChat className="user-profile__channel-link-icon" />
                          <span>{fetchData.user_username} {normalizedProfileType === 'artist' ? 'Studio' : 'VIP'}</span>
                        </button>
                      </div>
                    )}
                                        
                    <DisplayLocation userLocation={fetchData.user_profile_info.user_location}/>

                    <ProfileStats fetchData={fetchData} />

                    <ProfileButton
                      fetchData={fetchData}
                      setFetchData={setFetchData}
                      internal={internal}
                    />

                    <ProfileTopButtons fetchData={fetchData} internal={internal} />
                  </div>

                  <ProfileNavigation fetchData={fetchData} />

                  <div className="user-profile__outlet-container">
                    <div className="user-profile__outlet-inner">
                      <Outlet />
                    </div>
                  </div>
                </div>
              </div>
            </PullToRefresh>
          </AppNavigationPage>
          {shouldShowAskAiButton && (
            <>
              <button
                type="button"
                className={`sea-widget__launcher${isProfileAiOpen ? ' sea-widget__launcher--hidden' : ''}`}
                aria-label={t('buttons.ask_ai')}
                onClick={() => setIsProfileAiOpen(true)}
              >
                <ChatbotIcon className="sea-widget__launcher-icon" aria-hidden />
              </button>
              <GenericPopup
                open={isProfileAiOpen}
                className="sea-widget__panel"
                backgroundClassName="sea-widget__overlay"
                onBackgroundClick={() => setIsProfileAiOpen(false)}
              >
                <ProfileAskAi
                  onClose={() => setIsProfileAiOpen(false)}
                  assistantName={aiCapability.assistant_name}
                  mode={isGalleryProfile ? 'gallery' : 'artist'}
                  targetId={fetchData?._id || fetchData?.user_id || null}
                />
              </GenericPopup>
            </>
          )}
        </ProfileDataContext.Provider>

        {/* Channels selection popup */}
        <GenericPopup
          open={channelsPopupOpen}
          onCloseAction={() => {
            resetChannelsPopupState();
            setChannelsPopupOpen(false);
          }}
          className="user-profile__channels-popup remove-scrollbar"
          opacity
        >
          <div className="channels-modal">
            <div className="user-profile__channels-header element-non-selectable">
              {Boolean(editingChannelId) && (
                <BackArrowIcon
                  className="channels-modal__go-back"
                  onClick={() => {
                    setEditingChannelId(null);
                    setCoverPreviewUrl(null);
                    setSelectedCoverFile(null);
                    setOpenCoverCrop(false);
                    setUploadTarget(null);
                  }}
                />
              )}
              <h3 className="user-profile__channels-title">
                {creatingNew ? 'New VIP channel' : `${fetchData.user_username} VIP`}
              </h3>
              <XIcon
                className="channels-modal__close"
                onClick={() => {
                  resetChannelsPopupState();
                  setChannelsPopupOpen(false);
                }}
              />
            </div>
            {ALLOW_MANUAL_CHANNEL_MANAGEMENT && internal && !creatingNew && !editingChannelId && (
              <div className="user-profile__channel-add-row">
                <ContinueButton
                  type="button"
                  className="user-profile__channel-add-btn"
                  onClick={() => {
                    setCreatingNew(true);
                    setNewDraft({
                      title: '',
                      type: 'public',
                      description: '',
                      image: '',
                      price: '',
                      currency: DEFAULT_CHANNEL_CURRENCY,
                      write_perms: 'everyone'
                    });
                    setCoverPreviewUrl(null);
                  }}
                  disabled={channelsLoading}
                >
                    New VIP Channel
                </ContinueButton>
              </div>
            )}
            {/* Hidden uploader available for both create and edit flows */}
            <ImageUploading
              maxNumber={1}
              onChange={(images) => {
                if (images?.[0]?.file) {
                  setSelectedCoverFile(images[0].file);
                  setOpenCoverCrop(true);
                }
              }}
              onError={() => { /* noop minimal */ }}
              maxFileSize={15000000}
            >
              {({ onImageUpload }) => {
                imageUploadRef.current = onImageUpload;
                return null;
              }}
            </ImageUploading>

            <div className="user-profile__channels-list">
              {ALLOW_MANUAL_CHANNEL_MANAGEMENT && internal && creatingNew && (
                <div className="user-profile__channel-item user-profile__channel-item--creating">
                  <div className="user-profile__channel-edit-fields">
                    {/* Cover selector + cropper (16:9) */}
                    <div className="user-profile__channel-cover">
                      <div className="user-profile__channel-cover-header">
                        <span>Channel cover (16:9)</span>
                        {Boolean(coverPreviewUrl || newDraft.image) && (
                          <button
                            type="button"
                            className="user-profile__channel-cover-remove"
                            onClick={() => {
                              setCoverPreviewUrl(null);
                              setSelectedCoverFile(null);
                              setOpenCoverCrop(false);
                              setNewDraft(d => ({ ...d, image: '' }));
                            }}
                            aria-label="Remove cover"
                            title="Remove cover"
                          >
                            <DeleteIcon className="user-profile__channel-cover-remove-icon" />
                          </button>
                        )}
                      </div>
                      <div
                        className="user-profile__channel-cover-upload-trigger"
                        role="button"
                        tabIndex={0}
                        onClick={() => { setUploadTarget('new'); imageUploadRef.current && imageUploadRef.current(); }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setUploadTarget('new');
                            if (imageUploadRef.current) imageUploadRef.current();
                          }
                        }}
                      >
                        {coverPreviewUrl ? (
                          <UserProfileImage
                            className="user-profile__channel-cover-preview"
                            image={coverPreviewUrl}
                            loading={coverUploading}
                            typeOfProfile="gallery"
                            width={640}
                            height={360}
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="user-profile__channel-cover-placeholder">
                            <span>Add cover</span>
                          </div>
                        )}
                      </div>
                      {/* Crop popup */}
                      {openCoverCrop && selectedCoverFile && (
                        <PopupUpdateArticleCover
                          open={openCoverCrop}
                          image={selectedCoverFile}
                          exitCrop={() => setOpenCoverCrop(false)}
                          setCroppedFile={handleCoverCropped}
                          setCroppedUrl={setCoverPreviewUrl}
                          headerText="Update Channel Cover"
                          instructionsText="Adjust and crop your channel cover"
                        />
                      )}
                    </div>
                    <PublicFormInput
                      element="channel_title_new"
                      placeholder="Channel title"
                      value={newDraft.title}
                      onChange={(e)=>setNewDraft(d=>({ ...d, title: e.target.value }))}
                    />
                    <PublicFormInput
                      type="select"
                      element="channel_type_new"
                      placeholder="Type"
                      value={newDraft.type}
                      selectOptions={{ type: 'normal', options: ['public','followers','subscribers','collectors'] }}
                      onChange={(val)=>setNewDraft(d=>({ ...d, type: val }))}
                    />
                    {newDraft.type === 'subscribers' && (
                      <div className="user-profile__channel-price-row">
                        <PublicFormInput
                          className="user-profile__channel-price-currency"
                          type="select"
                          element="channel_currency_new"
                          placeholder="Currency"
                          selectOptions={{ options: CHANNEL_CURRENCY_OPTIONS }}
                          value={newDraft.currency}
                          onChange={(value)=>setNewDraft(d=>({ ...d, currency: value }))}
                        />
                        <PublicFormInput
                          className="user-profile__channel-price-value"
                          type="number"
                          element="channel_price_new"
                          placeholder="Monthly price"
                          min={0}
                          step="0.01"
                          value={newDraft.price}
                          onChange={(e)=>setNewDraft(d=>({ ...d, price: e.target.value }))}
                        />
                      </div>
                    )}
                    <PublicFormInput
                      type="select"
                      element="channel_write_perms_new"
                      placeholder="Who can write"
                      value={newDraft.write_perms}
                      selectOptions={{ type: 'normal', options: ['everyone','none','collectors', 'subscribers'] }}
                      onChange={(val)=>setNewDraft(d=>({ ...d, write_perms: val }))}
                    />
                    <PublicFormInput
                      type="textarea"
                      element="channel_description_new"
                      placeholder="Description (optional)"
                      value={newDraft.description}
                      onChange={(e)=>setNewDraft(d=>({ ...d, description: e.target.value }))}
                      autoResize
                      maxRows={4}
                    />
                  </div>
                  <div className="user-profile__channel-actions user-profile__channel-actions--creating">
                    <ContinueButton
                      type="button"
                      onClick={async () => {
                        setChannelsLoading(true);
                        try {
                          if (newDraft.type === 'subscribers' && (!newDraft.price || Number(newDraft.price) <= 0)) {
                            alert('Please set a valid monthly price for subscribers channels.');
                            setChannelsLoading(false);
                            return;
                          }
                          const payload = {
                            title: (newDraft.title || '').trim(),
                            type: newDraft.type,
                            description: newDraft.description,
                            image: newDraft.image || '',
                            write_perms: newDraft.write_perms,
                            ...(newDraft.type === 'subscribers' ? { price: Number(newDraft.price) } : {})
                          };
                          const { response } = await ChannelsService.create(payload);
                          if (response?.ok) {
                            await fetchChannelsForOwner();
                            setCreatingNew(false);
                            setCoverPreviewUrl(null);
                            setNewDraft({
                              title: '',
                              type: 'public',
                              description: '',
                              image: '',
                              price: '',
                              currency: DEFAULT_CHANNEL_CURRENCY,
                              write_perms: 'everyone'
                            });
                          }
                        } finally {
                          setChannelsLoading(false);
                        }
                      }}
                    >
                      Create
                    </ContinueButton>
                  </div>
                </div>
              )}
              {channelsList.map((ch) => {
                const isEditing = editingChannelId === ch._id;
                return (
                  <div
                    key={ch._id}
                    className={`user-profile__channel-item ${!isEditing ? 'user-profile__channel-item--read' : 'user-profile__channel-item--creating'}`}
                    onClick={!isEditing ? () => { handleOpenChannel(ch); } : undefined}
                    role={!isEditing ? 'button' : undefined}
                    tabIndex={!isEditing ? 0 : undefined}
                    onKeyDown={!isEditing ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenChannel(ch);
                      }
                    } : undefined}
                  >
                    <div className="user-profile__channel-info">
                      {!isEditing ? (
                        <>
                          {ch.title && (
                            <span className="user-profile__channel-title">{ch.title}</span>
                          )}
                          <span className="user-profile__channel-type">Access: VIP</span>
                          {ch.description ? (
                            <span className="user-profile__channel-desc">{ch.description}</span>
                          ) : null}
                          {ch.type === 'subscribers' && ch.price != null && (
                            <span className="user-profile__channel-desc">Price: {ch.price}</span>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="user-profile__channel-edit-fields">
                            {/* Edit cover selector + cropper (16:9) */}
                            <div className="user-profile__channel-cover">
                              <div className="user-profile__channel-cover-header">
                                <span>Channel cover (16:9)</span>
                                {Boolean((uploadTarget === 'edit' && coverPreviewUrl) || editDraft.image || ch.image) && (
                                  <button
                                    type="button"
                                    className="user-profile__channel-cover-remove"
                                    onClick={() => {
                                      setCoverPreviewUrl(null);
                                      setSelectedCoverFile(null);
                                      setOpenCoverCrop(false);
                                      setEditDraft(d => ({ ...d, image: '' }));
                                    }}
                                    aria-label="Remove cover"
                                    title="Remove cover"
                                  >
                                    <DeleteIcon className="user-profile__channel-cover-remove-icon" />
                                  </button>
                                )}
                              </div>
                              <div
                                className="user-profile__channel-cover-upload-trigger"
                                role="button"
                                tabIndex={0}
                                onClick={() => { setUploadTarget('edit'); if (imageUploadRef.current) imageUploadRef.current(); }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setUploadTarget('edit');
                                    if (imageUploadRef.current) imageUploadRef.current();
                                  }
                                }}
                              >
                                {(uploadTarget === 'edit' && coverPreviewUrl) ? (
                                  <UserProfileImage
                                    className="user-profile__channel-cover-preview"
                                    image={coverPreviewUrl}
                                    loading={coverUploading}
                                    typeOfProfile="gallery"
                                    width={640}
                                    height={360}
                                    style={{ objectFit: 'cover' }}
                                  />
                                ) : ((editDraft.image || ch.image) ? (
                                  <UserProfileImage
                                    className="user-profile__channel-cover-preview"
                                    image={editDraft.image || ch.image}
                                    typeOfProfile="gallery"
                                    width={640}
                                    height={360}
                                    style={{ objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div className="user-profile__channel-cover-placeholder">
                                    <span>Add cover</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <PublicFormInput
                              element={`channel_title_${ch._id}`}
                              placeholder="Channel title"
                              value={editDraft.title}
                              onChange={(e)=>setEditDraft(d=>({ ...d, title: e.target.value }))}
                            />
                            <PublicFormInput
                              type="select"
                              element={`channel_type_${ch._id}`}
                              placeholder="Type"
                              value={editDraft.type}
                              selectOptions={{ type: 'normal', options: ['public','followers','subscribers','collectors'] }}
                              onChange={(val)=>setEditDraft(d=>({ ...d, type: val }))}
                            />
                            {editDraft.type === 'subscribers' && (
                              <div className="user-profile__channel-price-row">
                                <PublicFormInput
                                  className="user-profile__channel-price-currency"
                                  type="select"
                                  element={`channel_currency_${ch._id}`}
                                  placeholder="Currency"
                                  selectOptions={{ options: CHANNEL_CURRENCY_OPTIONS }}
                                  value={editDraft.currency}
                                  onChange={(value)=>setEditDraft(d=>({ ...d, currency: value }))}
                                />
                                <PublicFormInput
                                  className="user-profile__channel-price-value"
                                  type="number"
                                  element={`channel_price_${ch._id}`}
                                  placeholder="Monthly price"
                                  min={0}
                                  step="0.01"
                                  value={editDraft.price}
                                  onChange={(e)=>setEditDraft(d=>({ ...d, price: e.target.value }))}
                                />
                              </div>
                            )}
                            <PublicFormInput
                              type="select"
                              element={`channel_write_perms_${ch._id}`}
                              placeholder="Who can write"
                              value={editDraft.write_perms}
                              selectOptions={{ type: 'normal', options: ['everyone','none','collectors'] }}
                              onChange={(val)=>setEditDraft(d=>({ ...d, write_perms: val }))}
                            />
                            <PublicFormInput
                              type="textarea"
                              element={`channel_description_${ch._id}`}
                              placeholder="Description (optional)"
                              value={editDraft.description}
                              onChange={(e)=>setEditDraft(d=>({ ...d, description: e.target.value }))}
                              autoResize
                              maxRows={4}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    <div className={`user-profile__channel-actions ${isEditing ? 'user-profile__channel-actions--creating' : ''}`}>
                      {ALLOW_MANUAL_CHANNEL_MANAGEMENT && internal && !isEditing && (
                        <button
                          type="button"
                          className="user-profile__channel-edit-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingChannelId(ch._id);
                            setEditDraft({
                              title: ch.title || '',
                              type: ch.type || 'public',
                              description: ch.description || '',
                              image: ch.image || '',
                              price: ch.price ?? '',
                              currency: resolveChannelCurrency(ch.currency),
                              write_perms: ch.write_perms || 'everyone'
                            });
                            setCoverPreviewUrl(null);
                            setSelectedCoverFile(null);
                            setOpenCoverCrop(false);
                            setUploadTarget(null);
                          }}
                          title="Edit"
                        >
                          <BsPencil/>
                        </button>
                      )}
                      {ALLOW_MANUAL_CHANNEL_MANAGEMENT && internal && isEditing && (
                        <>
                          <ContinueButton
                            type="button"
                            onClick={async () => {
                              setChannelsLoading(true);
                              try {
                                const payload = {
                                  title: (editDraft.title || '').trim(),
                                  type: editDraft.type,
                                  description: editDraft.description,
                                  image: editDraft.image || ch.image || '',
                                  write_perms: editDraft.write_perms,
                                  ...(editDraft.type === 'subscribers' ? { price: Number(editDraft.price) } : { price: undefined })
                                };
                                const { response } = await ChannelsService.update(ch._id, payload);
                                if (response?.ok) {
                                  await fetchChannelsForOwner();
                                  setEditingChannelId(null);
                                  setCoverPreviewUrl(null);
                                  setSelectedCoverFile(null);
                                  setOpenCoverCrop(false);
                                  setUploadTarget(null);
                                }
                              } finally {
                                setChannelsLoading(false);
                              }
                            }}
                          >
                            Save
                          </ContinueButton>
                        </>
                      )}
                      {ALLOW_MANUAL_CHANNEL_MANAGEMENT && internal && !isEditing && (
                        <button
                          type="button"
                          className="user-profile__channel-delete-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDeleteChannelDialog(ch);
                          }}
                          title="Delete"
                        >
                          <BsTrash/>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </GenericPopup>

        <GenericPopup
          open={deleteDialogOpen}
          className="user-profile__delete-channel-popup"
          opacity
        >
          <div className="user-profile__delete-channel-header">
            <h3 className="user-profile__delete-channel-title">Delete channel</h3>
            <XIcon className="user-profile__delete-channel-close" onClick={closeDeleteChannelDialog} />
          </div>

          <p className="user-profile__delete-channel-text">
            Are you sure you want to delete the{' '}
            <span className="user-profile__delete-channel-name">
              {channelPendingDelete?.title || 'this'}
            </span>{' '}
            channel?
          </p>

          <div className="user-profile__delete-channel-actions">
            <ContinueButton
              onClick={confirmDeleteChannel}
              className="font-size-medium"
              disabled={deletingChannel}
            >
              Yes, delete
            </ContinueButton>
          </div>
        </GenericPopup>
        {previewChannel && (
          <SubscriptionPreview
            channelId={previewChannel._id}
            ownerUsername={fetchData.user_username}
            onClose={() => setPreviewChannel(null)}
          />
        )}
      </>
    );
  }
}

export default function UserProfile({ internal = false }) {
  const { state } = useStateHandler();
  const { username } = useParams();
  const profileUsername = internal ? state.user_session.user_username : username;

  /** Prevents a user from seeing an external view of his own profile */
  if (!internal && state.user_session?.user_username === username) {
    return (
      <Navigate to="/profile" replace />
    );
  } else {
    /** Makes the component refresh on username change */
    return (
      <UserProfileRenderView
        key={profileUsername}
        profileUsername={profileUsername}
        internal={internal}
      />
    );
  }
}
